import React, { useCallback, useState } from 'react';
import {
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import {
  APPLY_NOW_COPY,
  CADENCE_OPTIONS,
  useTodoListForm,
} from '@/hooks/admin/useTodoListForm';
import type { Cadence, TodoList } from '@/types/admin/todoLists';
import { TodoTargetPicker } from './TodoTargetPicker';

interface Props {
  mode: 'create' | 'edit';
  propertyId: number;
  initial?: TodoList;
  isSubmitting?: boolean;
  /** Inline, non-crashing error (e.g. a 409 list_archived / no_current_occurrence). */
  submitError?: string | null;
  onSubmit: (payload: any) => void;
  onCancel: () => void;
}

/**
 * The create/edit to-do list form. RHF-driven (via `useTodoListForm`): title,
 * cadence (3-way), XOR target (role|person) through `TodoTargetPicker`, an
 * optional expiry (`endsOn`) date, and an ordered tasks editor (add / rename /
 * remove-with-undo / reorder with up-down controls — no drag library). On a
 * valid submit it builds the create/update payload and calls `onSubmit` ONCE. In
 * edit mode the "apply now" toggle (default ON) rides in that single update
 * payload as `applyNow`, so the current occurrence is re-synced atomically
 * server-side — there is no separate apply-now request.
 */
export const TodoListForm: React.FC<Props> = ({
  mode,
  propertyId,
  initial,
  isSubmitting = false,
  submitError,
  onSubmit,
  onCancel,
}) => {
  const { redesign } = useAdminColors();
  const form = useTodoListForm({ mode, propertyId, initial });
  // Default ON in edit mode: applying edits to the live checklist is the common
  // intent; users uncheck to make the change forward-only. (Create has no toggle.)
  const [applyNow, setApplyNow] = useState(mode === 'edit');

  const handleSubmit = useCallback(() => {
    if (!form.validate()) return;
    const payload =
      mode === 'edit'
        ? form.buildUpdatePayload(applyNow)
        : form.buildCreatePayload();
    onSubmit(payload);
  }, [applyNow, form, mode, onSubmit]);

  const inputStyle = [
    styles.input,
    { color: redesign.text.primary, borderColor: redesign.card.border },
  ];
  const applyNowBg = applyNow ? redesign.accent.greenBg : 'transparent';
  const submitStyle = {
    backgroundColor: redesign.accent.purpleLight,
    opacity: isSubmitting ? 0.7 : 1,
  };

  return (
    <View style={styles.container}>
      {/* Title */}
      <View style={styles.field}>
        <Text style={[styles.label, { color: redesign.text.secondary }]}>
          Title
        </Text>
        <TextInput
          testID="todo-list-title-input"
          value={form.title}
          onChangeText={value => form.setField('title', value)}
          placeholder="e.g. Morning rounds"
          placeholderTextColor={redesign.text.muted}
          style={inputStyle}
        />
        {form.errors.title ? (
          <Text style={[styles.error, { color: redesign.accent.red }]}>
            {form.errors.title}
          </Text>
        ) : null}
      </View>

      {/* Cadence */}
      <View style={styles.field}>
        <Text style={[styles.label, { color: redesign.text.secondary }]}>
          Cadence
        </Text>
        <View style={styles.cadenceRow}>
          {CADENCE_OPTIONS.map(option => {
            const selected = form.cadence === option.value;
            const optionStyle = {
              borderColor: selected
                ? redesign.accent.purpleLight
                : redesign.card.border,
              backgroundColor: selected
                ? redesign.accent.blueBg
                : 'transparent',
            };
            const textColor = selected
              ? redesign.text.primary
              : redesign.text.secondary;
            return (
              <Pressable
                key={option.value}
                testID={`cadence-option-${option.value}`}
                onPress={() =>
                  form.setField('cadence', option.value as Cadence)
                }
                style={[styles.cadenceOption, optionStyle]}
              >
                <Text style={[styles.cadenceText, { color: textColor }]}>
                  {option.label}
                </Text>
              </Pressable>
            );
          })}
        </View>
      </View>

      {/* XOR target */}
      <TodoTargetPicker
        propertyId={propertyId}
        roleId={form.roleId}
        assigneeAccountId={form.assigneeAccountId}
        onSelectRole={form.selectRole}
        onSelectPerson={form.selectPerson}
        error={form.errors.target}
      />

      {/* Dates */}
      <View style={styles.datesRow}>
        <View style={styles.dateField}>
          <Text style={[styles.label, { color: redesign.text.secondary }]}>
            Starts on (optional)
          </Text>
          <TextInput
            testID="todo-list-starts-on-input"
            value={form.startsOn}
            onChangeText={value => form.setField('startsOn', value)}
            placeholder="YYYY-MM-DD"
            placeholderTextColor={redesign.text.muted}
            autoCapitalize="none"
            style={inputStyle}
          />
        </View>
        <View style={styles.dateField}>
          <Text style={[styles.label, { color: redesign.text.secondary }]}>
            Expires on (optional)
          </Text>
          <TextInput
            testID="todo-list-ends-on-input"
            value={form.endsOn}
            onChangeText={value => form.setField('endsOn', value)}
            placeholder="YYYY-MM-DD"
            placeholderTextColor={redesign.text.muted}
            autoCapitalize="none"
            style={inputStyle}
          />
        </View>
      </View>
      {form.errors.window ? (
        <Text style={[styles.error, { color: redesign.accent.red }]}>
          {form.errors.window}
        </Text>
      ) : null}

      {/* Tasks editor */}
      <View style={styles.field}>
        <View style={styles.tasksHeader}>
          <Text style={[styles.label, { color: redesign.text.secondary }]}>
            Tasks
          </Text>
          <TouchableOpacity
            testID="task-add-button"
            onPress={form.addTask}
            style={[styles.addButton, { borderColor: redesign.card.border }]}
          >
            <Text
              style={[styles.addButtonText, { color: redesign.text.primary }]}
            >
              + Add task
            </Text>
          </TouchableOpacity>
        </View>

        {form.isTasksEmpty ? (
          <Text style={[styles.warning, { color: redesign.text.muted }]}>
            No tasks yet — you can still save an empty list and add tasks later.
          </Text>
        ) : null}

        {form.rows.map((row, index) =>
          row.removed ? (
            // Soft-removed existing row: kept visible but greyed + struck through
            // with an Undo affordance, so a removal can be reversed before save.
            <View key={row.key} style={styles.taskRow}>
              <Text
                numberOfLines={1}
                style={[styles.removedTaskTitle, { color: redesign.text.muted }]}
              >
                {row.title}
              </Text>
              <TouchableOpacity
                testID={`task-undo-${index}`}
                onPress={() => form.undoRemoveTask(index)}
                style={styles.removeButton}
              >
                <Text
                  style={[styles.removeText, { color: redesign.text.secondary }]}
                >
                  Undo
                </Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View key={row.key} style={styles.taskRow}>
              <View style={styles.taskReorder}>
                <TouchableOpacity
                  testID={`task-move-up-${index}`}
                  disabled={index === 0}
                  onPress={() => form.moveTask(index, -1)}
                  style={styles.reorderButton}
                >
                  <Text
                    style={[
                      styles.reorderText,
                      {
                        color:
                          index === 0
                            ? redesign.text.dim
                            : redesign.text.secondary,
                      },
                    ]}
                  >
                    ↑
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  testID={`task-move-down-${index}`}
                  disabled={index === form.rows.length - 1}
                  onPress={() => form.moveTask(index, 1)}
                  style={styles.reorderButton}
                >
                  <Text
                    style={[
                      styles.reorderText,
                      {
                        color:
                          index === form.rows.length - 1
                            ? redesign.text.dim
                            : redesign.text.secondary,
                      },
                    ]}
                  >
                    ↓
                  </Text>
                </TouchableOpacity>
              </View>
              <TextInput
                testID={`task-title-input-${index}`}
                value={row.title}
                onChangeText={value => form.renameTask(index, value)}
                placeholder="Task title"
                placeholderTextColor={redesign.text.muted}
                style={[
                  styles.input,
                  styles.taskInput,
                  {
                    color: redesign.text.primary,
                    borderColor: redesign.card.border,
                  },
                ]}
              />
              <TouchableOpacity
                testID={`task-remove-${index}`}
                onPress={() => form.removeTask(index)}
                style={styles.removeButton}
              >
                <Text
                  style={[styles.removeText, { color: redesign.accent.red }]}
                >
                  Remove
                </Text>
              </TouchableOpacity>
            </View>
          ),
        )}
      </View>

      {/* Apply now (edit only) — rides in the single update payload; post-save
          confirmation now lives on the checklist screen the user lands on. */}
      {mode === 'edit' ? (
        <View
          style={[styles.applyNowBox, { borderColor: redesign.card.border }]}
        >
          <Pressable
            testID="apply-now-toggle"
            onPress={() => setApplyNow(prev => !prev)}
            style={styles.applyNowRow}
          >
            <View
              style={[
                styles.checkbox,
                {
                  borderColor: redesign.card.border,
                  backgroundColor: applyNowBg,
                },
              ]}
            >
              {applyNow ? (
                <Text
                  style={[
                    styles.checkboxMark,
                    { color: redesign.accent.green },
                  ]}
                >
                  ✓
                </Text>
              ) : null}
            </View>
            <View style={styles.applyNowCopy}>
              <Text
                style={[styles.applyNowTitle, { color: redesign.text.primary }]}
              >
                {APPLY_NOW_COPY}
              </Text>
              <Text
                style={[styles.applyNowHint, { color: redesign.text.muted }]}
              >
                Applies these changes to the current period’s checklist too.
                Uncheck to only affect future periods.
              </Text>
            </View>
          </Pressable>
        </View>
      ) : null}

      {/* Inline submit error (409 list_archived / no_current_occurrence, etc.) */}
      {submitError ? (
        <Text
          testID="todo-list-submit-error"
          style={[styles.error, { color: redesign.accent.red }]}
        >
          {submitError}
        </Text>
      ) : null}

      {/* Actions */}
      <View style={styles.actions}>
        <TouchableOpacity
          testID="todo-list-cancel"
          onPress={onCancel}
          style={[
            styles.secondaryButton,
            { borderColor: redesign.card.border },
          ]}
        >
          <Text
            style={[styles.secondaryText, { color: redesign.text.secondary }]}
          >
            Cancel
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          testID="todo-list-submit"
          onPress={handleSubmit}
          disabled={isSubmitting}
          style={[styles.primaryButton, submitStyle]}
        >
          <Text style={styles.primaryText}>
            {mode === 'edit' ? 'Save changes' : 'Create list'}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: { gap: 16 },
  field: { gap: 8 },
  label: { fontSize: 14, fontWeight: '700' },
  input: {
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 15,
  },
  error: { fontSize: 12 },
  warning: { fontSize: 13, fontStyle: 'italic' },
  cadenceRow: { flexDirection: 'row', gap: 8 },
  cadenceOption: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
  },
  cadenceText: { fontSize: 14, fontWeight: '600' },
  datesRow: { flexDirection: 'row', gap: 12 },
  dateField: { flex: 1, gap: 8 },
  tasksHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  addButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    borderWidth: 1,
  },
  addButtonText: { fontSize: 13, fontWeight: '600' },
  taskRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  taskReorder: { gap: 2 },
  reorderButton: { paddingHorizontal: 6, paddingVertical: 2 },
  reorderText: { fontSize: 16, fontWeight: '700' },
  taskInput: { flex: 1 },
  removedTaskTitle: { flex: 1, textDecorationLine: 'line-through' },
  removeButton: { paddingHorizontal: 8, paddingVertical: 6 },
  removeText: { fontSize: 13, fontWeight: '600' },
  applyNowBox: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    gap: 8,
  },
  applyNowRow: { flexDirection: 'row', gap: 10, alignItems: 'flex-start' },
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 5,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 1,
  },
  checkboxMark: { fontSize: 14, fontWeight: '700' },
  applyNowCopy: { flex: 1, gap: 2 },
  applyNowTitle: { fontSize: 14, fontWeight: '700' },
  applyNowHint: { fontSize: 12 },
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 10,
    marginTop: 4,
  },
  secondaryButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
  },
  secondaryText: { fontSize: 14, fontWeight: '600' },
  primaryButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
  },
  primaryText: { fontSize: 14, fontWeight: '600', color: '#FFFFFF' },
});
